<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconFlask, IconInfoCircle, IconShieldCheck } from '@tabler/icons-vue'
import { Badge } from '@ui'
import InfoPopover from '@/ui-delta/InfoPopover.vue'
import type { AnalystAnswer, AnswerClaimClass } from '@/api/analyst'
import { badgeVariant, type BadgeTone } from '@/lib/badge-variant'
import { factLabel, factValue, metricName, needsText, readingText, subjectName } from '@/lib/analyst'

/**
 * One answer, as P4 §5 lays it out: each statement with its claim class,
 * the metric it cites and the cases behind it; what limits it; and whether
 * its numbers were checked. The words are the model's, passed by the hard
 * gate; everything around them is this product's own, from codes.
 *
 * The shield is drawn exactly when the engine attached a verification
 * result, which it does only when the gate passed in an OPERATIONAL
 * workspace (P4 §5.2, S0 §50 rule 17). A DEMONSTRATION workspace says so
 * instead, and never shows the shield.
 *
 * What changes how a statement is read stays in view: an association is not
 * a cause (P4 §5.1), a metric's limits, the demonstration marker, what could
 * not be measured. What only explains a label (what a hypothesis is) is one
 * hover away (Dao, 1 Oct 2026).
 */
// A saved answer leaves out its asker's declared function (DECISIONS.md #32); nothing here reads it.
const props = defineProps<{ answer: Omit<AnalystAnswer, 'readerFunction'> }>()
const { t, te, locale } = useI18n()

const CLAIM_TONES: Record<AnswerClaimClass, BadgeTone> = {
  DERIVED_FACT: 'accent',
  ASSOCIATION: 'neutral',
  DIAGNOSTIC_HYPOTHESIS: 'warning',
}

const count = (value: number): string => new Intl.NumberFormat(locale.value).format(value)
const coefficient = (value: number): string =>
  new Intl.NumberFormat(locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
const day = (iso: string): string => new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium' }).format(new Date(iso))

const outcomeNotice = computed(() => (props.answer.outcome === 'ANSWERED' ? null : t(`analyst.outcomes.${props.answer.outcome}`)))
// Said where there are numbers to check: a clinical redirect or a question turned away carries none.
const demonstration = computed(
  () => props.answer.dataProvenanceMode === 'DEMONSTRATION' && (props.answer.statements.length > 0 || props.answer.facts.length > 0),
)
const limits = computed(() =>
  props.answer.metrics.flatMap((reading) => {
    const text = readingText(reading, t)
    return text ? [{ metricId: reading.metricId, text }] : []
  }),
)
/** Notices that change how a statement is read are shown with it; the rest explain its label, on hover. */
const IN_VIEW = new Set(['ASSOCIATION_NOT_CAUSATION'])
// An answer with findings says in one line that part of the question could not be measured; one without, why.
const partly = computed(() => props.answer.statements.length > 0)
const notMeasurable = computed(() =>
  props.answer.notMeasurable.map((item) =>
    [
      t(`analyst.notMeasurable.${item.reason}`, { subject: subjectName(item.subject, t, te) }),
      needsText(item.missing, t, te, locale.value),
    ]
      .filter(Boolean)
      .join(' '),
  ),
)
// A number whose group the screen cannot name is left out rather than shown under a code.
const facts = computed(() =>
  props.answer.facts.flatMap((fact) => {
    const label = factLabel(fact, props.answer.period, t, te, locale.value)
    return label === null ? [] : [{ factId: fact.factId, label, value: factValue(fact, locale.value, t) }]
  }),
)
</script>

<template>
  <div class="answer" data-test="analyst-answer">
    <p v-if="outcomeNotice" class="answer__outcome" data-test="analyst-outcome">{{ outcomeNotice }}</p>

    <ul v-if="answer.statements.length > 0" class="answer__statements">
      <li v-for="(statement, index) in answer.statements" :key="index" class="statement" data-test="analyst-statement">
        <p class="statement__text">{{ statement.text }}</p>
        <div class="statement__tags">
          <Badge
            size="sm"
            :variant="badgeVariant(CLAIM_TONES[statement.claimClass])"
            :label="t(`analyst.claims.${statement.claimClass}`)"
            :title="statement.claimClass"
            data-test="analyst-claim"
          />
          <Badge
            v-for="metricId in statement.metricIds"
            :key="metricId"
            size="sm"
            :variant="badgeVariant('neutral')"
            :label="metricName(metricId, t)"
            :title="metricId"
          />
          <Badge
            v-if="statement.sampleN !== null"
            size="sm"
            :variant="badgeVariant('neutral')"
            :label="t('analyst.sample', { n: count(statement.sampleN) })"
          />
          <Badge
            v-if="statement.statistic"
            size="sm"
            :variant="badgeVariant('neutral')"
            :label="t('analyst.statistic', { value: coefficient(statement.statistic.value) })"
          />
          <InfoPopover
            v-for="notice in statement.notices.filter((n) => !IN_VIEW.has(n))"
            :key="notice"
            :label="t('analyst.notices.help')"
            label-hidden
            data-test="analyst-notice-help"
          >
            <template #icon><IconInfoCircle :size="16" aria-hidden="true" /></template>
            {{ t(`analyst.notices.${notice}`) }}
          </InfoPopover>
        </div>
        <p v-for="notice in statement.notices.filter((n) => IN_VIEW.has(n))" :key="notice" class="statement__notice">
          {{ t(`analyst.notices.${notice}`) }}
        </p>
      </li>
    </ul>

    <p v-if="partly && notMeasurable.length > 0" class="answer__limit answer__partly" data-test="analyst-not-measurable-summary">
      <span>{{ t('analyst.notMeasurable.partly', { n: notMeasurable.length }, notMeasurable.length) }}</span>
      <InfoPopover :label="t('analyst.notMeasurable.which')" label-hidden>
        <template #icon><IconInfoCircle :size="16" aria-hidden="true" /></template>
        <ul class="answer__missing-detail">
          <li v-for="line in notMeasurable" :key="line">{{ line }}</li>
        </ul>
      </InfoPopover>
    </p>
    <ul v-else-if="notMeasurable.length > 0" class="answer__missing" data-test="analyst-not-measurable">
      <li v-for="line in notMeasurable" :key="line">{{ line }}</li>
    </ul>

    <p v-for="limit in limits" :key="limit.metricId" class="answer__limit" data-test="analyst-limit">{{ limit.text }}</p>

    <p v-for="suppression in answer.suppressions" :key="suppression.breakdown" class="answer__limit">
      {{ t('analyst.suppressed', { minimum: count(suppression.minimum), groups: count(suppression.groups) }) }}
    </p>

    <!-- S0 §26b.5: independent of the shield, and drawn whether or not the gate passed (P4 §5.2). -->
    <p v-for="marker in answer.attestedContext" :key="marker.attestedContextId" class="answer__attested" data-test="analyst-attested">
      <IconInfoCircle :size="16" aria-hidden="true" />
      {{ t('analyst.attested', { name: marker.attestedBy, date: day(marker.asOf) }) }}
    </p>

    <p v-if="answer.verification" class="answer__verified" data-test="analyst-verified">
      <IconShieldCheck :size="16" aria-hidden="true" />
      <strong>{{ t('analyst.verified.label') }}</strong>
      {{ t('analyst.verified.text') }}
    </p>
    <p v-else-if="demonstration" class="answer__demo" data-test="analyst-demonstration">
      <IconFlask :size="16" aria-hidden="true" />
      <strong>{{ t('workspace.demonstrationMarker') }}</strong>
      {{ t('analyst.notVerified') }}
    </p>

    <details v-if="facts.length > 0" class="answer__numbers" :open="answer.outcome === 'WITHHELD'">
      <summary>{{ t('analyst.numbers.title') }}</summary>
      <dl class="numbers">
        <div v-for="fact in facts" :key="fact.factId" class="numbers__row" data-test="analyst-fact">
          <dt>{{ fact.label }}</dt>
          <dd>{{ fact.value }}</dd>
        </div>
      </dl>
    </details>
  </div>
</template>

<style scoped>
.answer {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.answer__outcome {
  margin: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-lg);
  background: var(--surface-sunken);
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-primary);
}

.answer__partly {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.answer__missing-detail {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin: 0;
  padding-left: var(--spacing-lg);
}

.answer__statements {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  margin: 0;
  padding: 0;
  list-style: none;
}

.statement {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.statement__text {
  margin: 0;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-primary);
}

.statement__tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
}

.statement__notice,
.answer__limit,
.answer__missing {
  margin: 0;
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-tertiary);
}

.answer__missing {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  padding-inline-start: var(--spacing-xl);
}

.answer__attested,
.answer__verified,
.answer__demo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs);
  margin: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
}

.answer__attested {
  background: var(--alert-info-bg);
  color: var(--fg-primary);
}

.answer__verified {
  background: var(--alert-success-bg);
  color: var(--fg-primary);
}

.answer__verified :deep(svg) {
  color: var(--alert-success-accent);
}

.answer__demo {
  background: var(--alert-warning-bg);
  color: var(--fg-primary);
}

.answer__numbers summary {
  cursor: pointer;
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
}

.numbers {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  margin: var(--spacing-sm) 0 0;
}

.numbers__row {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-md);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
}

.numbers__row dt {
  color: var(--fg-secondary);
}

.numbers__row dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
  color: var(--fg-primary);
  white-space: nowrap;
}
</style>
